import React, { useContext } from 'react';
import classnames from 'classnames';
import Flex from '@wix/da-ds/pkg/Flex';
import { useDeviationViewContext } from '../../../../Context';
import { SecondaryActionBarContext } from '../../../ActionBarContext';
import ActionBarStrip from '../../../_partials/ActionBarStrip';
import SubscribersOnly from '../../../_partials/SubscribersOnly';
import { useDeviationAttributes } from '../../../../DeviationViews/hooks/useDeviationAttributes';
import {
  isTextualDeviation,
  isHyped,
} from '../../../../DeviationViews/_helpers';
import DeviationFlags from '../../../buttons/DeviationFlags';
import CommentButton from '../../../buttons/CommentButton';
import FavouriteButton from '../../../buttons/FavouriteButtonWithPopover';
import AddToGroupButton from '../../../buttons/AddToGroupButton/AddToGroupButtonWithPopover';
import MoreButton from '../../../buttons/MoreButton';
import EditButton from '../../../buttons/EditButton';
import EditBackgroundButton from '../../../buttons/EditBackgroundButton';
import DeleteButton from '../../../buttons/DeleteButton';
import ExpandButton from '../../../buttons/ExpandButton';
import FreeDownloadButton from '../../../buttons/DownloadButton/FreeDownloadButton';
import PremiumDownloadButton from '../../../buttons/DownloadButton/PremiumDownloadButton';
import AwardBadgeButton from '../../../buttons/AwardBadgeButton';
import SellYourArtLink from '../../../buttons/SellYourArtLink';
import FluidMargin from '../FluidMargin';
import AdoptSeller from '../../../buttons/AdoptableSeller';
import AdoptBuyer from '../../../buttons/AdoptableBuyer';
import AddToPrivateCollectionButtonWithPopover from '../../../buttons/AddToPrivateCollectionButtonWithPopover';
import { StandardDeviationElementPlacement } from '../../../ElementPlacementContext/variants/standard/types';
import { StandardDeviationElementPlacementContext } from '../../../ElementPlacementContext/variants/standard/context';
import { useElementPlacementForArea } from '../../../ElementPlacementContext/utils';
import BoostButton from '../../../buttons/BoostButton';
import { ActionBarProps as Props } from '../../../types';

import desktopActionBarStyles from '../DesktopActionBar.scss';

export const PrimaryDesktopActionBar: React.FC<
  React.PropsWithChildren<Props>
> = ({ deviation, isOwner, className }) => {
  const elementPlacementMap = useElementPlacementForArea(
    StandardDeviationElementPlacement.PrimaryBar,
    StandardDeviationElementPlacementContext
  );
  const { isTheater } = useDeviationViewContext();
  const { setTopActionBarRef } = useContext(SecondaryActionBarContext);
  const { hasFlag } = useDeviationAttributes(deviation, isOwner);
  const isTextual = isTextualDeviation(deviation);
  const hasBothBadges = deviation.dailyDeviation && isHyped(deviation);

  const badgeClassnames = classnames(
    desktopActionBarStyles['badge'],
    hasBothBadges && desktopActionBarStyles['both-badges'],
    elementPlacementMap.subscribe && desktopActionBarStyles['subs-badge']
  );

  return (
    <ActionBarStrip
      ref={setTopActionBarRef}
      className={classnames(
        desktopActionBarStyles['root'],
        isTheater
          ? desktopActionBarStyles['theater']
          : desktopActionBarStyles['normal-view'],
        isOwner && desktopActionBarStyles['owner'],
        isTextual && desktopActionBarStyles['literature'],
        className
      )}
    >
      <div className={desktopActionBarStyles['icons-wrapper']}>
        <div className={badgeClassnames}>
          {hasFlag && (
            <DeviationFlags
              className={desktopActionBarStyles['flag']}
              deviation={deviation}
              disableLabel={isTextual}
            />
          )}
          {elementPlacementMap.subscribe && (
            <SubscribersOnly
              deviation={deviation}
              className={desktopActionBarStyles['subscribers-only']}
              textClassName={desktopActionBarStyles['subscribers-only-text']}
            />
          )}
        </div>
        <FluidMargin index={1} />
        <div className={desktopActionBarStyles['icons-middle']}>
          <Flex alignItems="center">
            {elementPlacementMap.favourite && (
              <span
                className={classnames(
                  desktopActionBarStyles['icon'],
                  desktopActionBarStyles['icon-with-label']
                )}
              >
                <FavouriteButton
                  deviation={deviation}
                  actionBarType="primary"
                />
              </span>
            )}
            {elementPlacementMap.comment && (
              <span
                className={classnames(
                  desktopActionBarStyles['icon'],
                  desktopActionBarStyles['icon-with-label']
                )}
              >
                <CommentButton
                  deviation={deviation}
                  isOwner={isOwner}
                  actionBarType="primary"
                />
              </span>
            )}
          </Flex>
          {!isOwner && <FluidMargin index={4} />}
          <Flex alignItems="center">
            {elementPlacementMap.privateCollection && (
              <span className={desktopActionBarStyles['icon']}>
                <AddToPrivateCollectionButtonWithPopover
                  deviation={deviation}
                  actionBarType="primary"
                />
              </span>
            )}
            {elementPlacementMap.addToGroup && (
              <span className={desktopActionBarStyles['icon']}>
                <AddToGroupButton
                  deviation={deviation}
                  actionBarType="primary"
                />
              </span>
            )}
            {elementPlacementMap.awardBadge && (
              <span className={classnames(desktopActionBarStyles['icon'])}>
                <AwardBadgeButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.premiumDownload && (
              <span className={classnames(desktopActionBarStyles['icon'])}>
                <PremiumDownloadButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.freeDownload && (
              <span className={classnames(desktopActionBarStyles['icon'])}>
                <FreeDownloadButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.elementGroups.adoptableSeller
              .groupPlacement && (
              <AdoptSeller
                className={desktopActionBarStyles['icon']}
                deviation={deviation}
              />
            )}
            {elementPlacementMap.elementGroups.adoptableBuyer
              .groupPlacement && (
              <span className={desktopActionBarStyles['icon']}>
                <AdoptBuyer deviation={deviation} />
              </span>
            )}
          </Flex>
          {(elementPlacementMap.editDeviation ||
            elementPlacementMap.deleteDeviation ||
            elementPlacementMap.meta.hasPrimaryMoreActionsMenu) && (
            <FluidMargin index={2} />
          )}
          <Flex alignItems="center">
            {elementPlacementMap.boostDeviation && (
              <BoostButton
                deviation={deviation}
                className={desktopActionBarStyles['icon']}
              />
            )}
            {elementPlacementMap.sellDeviation && (
              <SellYourArtLink
                className={desktopActionBarStyles['icon']}
                deviation={deviation}
              />
            )}
            {elementPlacementMap.meta.hasPrimaryMoreActionsMenu && (
              <span className={desktopActionBarStyles['icon']}>
                <MoreButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.editBackground && (
              <span className={desktopActionBarStyles['icon']}>
                <EditBackgroundButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.deleteDeviation && (
              <span className={desktopActionBarStyles['icon']}>
                <DeleteButton deviation={deviation} />
              </span>
            )}
            {elementPlacementMap.editDeviation && (
              <span className={desktopActionBarStyles['icon']}>
                <EditButton deviation={deviation} />
              </span>
            )}
          </Flex>
        </div>
        <FluidMargin index={3} />
        <div className={desktopActionBarStyles['icons-right']}>
          {elementPlacementMap.fullscreen && (
            <span className={desktopActionBarStyles['icon']}>
              <ExpandButton deviation={deviation} />
            </span>
          )}
        </div>
      </div>
    </ActionBarStrip>
  );
};
PrimaryDesktopActionBar.displayName = 'PrimaryDesktopActionBar';

export default PrimaryDesktopActionBar;
